import React, { useState, useEffect } from 'react';
import { X, Shield, BarChart3, Megaphone } from 'lucide-react';
import { getConsent, saveConsent, acceptAll, acceptNecessaryOnly } from '../utils/consent';

const CookieSettingsModal = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    if (isOpen) {
      const consent = getConsent();
      if (consent) {
        setSettings({
          analytics: consent.analytics,
          marketing: consent.marketing
        });
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    saveConsent(settings.analytics, settings.marketing);
    onClose?.();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose?.();
  };

  const handleNecessaryOnly = () => {
    acceptNecessaryOnly();
    onClose?.();
  };

  if (!isOpen) return null;

  const categories = [
    {
      id: 'necessary',
      title: 'Notwendig',
      description: 'Für Login, Sicherheit und Grundfunktionen.',
      icon: Shield,
      required: true,
      enabled: true
    },
    {
      id: 'analytics',
      title: 'Statistik',
      description: 'Hilft uns zu verstehen, was gut funktioniert.',
      icon: BarChart3,
      required: false,
      enabled: settings.analytics
    },
    {
      id: 'marketing',
      title: 'Marketing',
      description: 'Für personalisierte Werbung. Aktuell nicht aktiv.',
      icon: Megaphone,
      required: false,
      enabled: settings.marketing
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Cookie-Einstellungen</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-sm mb-6 leading-relaxed">
            Wir nutzen Cookies, damit die Website zuverlässig funktioniert. Optional helfen uns 
            Statistik-Cookies, die Nutzung zu verstehen und die Seite zu verbessern. Du kannst 
            deine Auswahl jederzeit ändern.
          </p>

          {/* Categories */}
          <div className="space-y-4">
            {categories.map((category) => {
              const Icon = category.icon;
              
              return (
                <div
                  key={category.id}
                  className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-brand-blue" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{category.title}</h3>
                          {category.required && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              Immer aktiv
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>
                    </div>

                    {/* Toggle */}
                    <div className="ml-4">
                      {category.required ? (
                        <div className="w-12 h-6 bg-brand-green rounded-full flex items-center justify-end px-1">
                          <div className="w-4 h-4 bg-white rounded-full" />
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            if (category.id === 'analytics') {
                              setSettings({ ...settings, analytics: !settings.analytics });
                            } else if (category.id === 'marketing') {
                              setSettings({ ...settings, marketing: !settings.marketing });
                            }
                          }}
                          className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                            category.enabled
                              ? 'bg-brand-green justify-end'
                              : 'bg-gray-300 justify-start'
                          } px-1`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full shadow" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleSave}
            className="flex-1 px-6 py-3 bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold rounded-xl transition-colors"
          >
            Speichern
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 px-6 py-3 bg-brand-green hover:bg-brand-green-dark text-white font-semibold rounded-xl transition-colors"
          >
            Alle akzeptieren
          </button>
          <button
            onClick={handleNecessaryOnly}
            className="flex-1 px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl transition-colors border-2 border-gray-200"
          >
            Nur notwendige
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
